import store from '../state';
import { cleanUrl, appendAuthToken } from '../config';
import authService from './AuthService';
import ManifestTimelineHelper from './ManifestTimelineHelper';



export function parseIsoScanToMs(scan: string): number {
  if (!scan) return 0;
  const clean = scan.replace(/[-_]/g, '');
  if (clean.length >= 14) {
    const yr = parseInt(clean.slice(0, 4), 10);
    const mo = parseInt(clean.slice(4, 6), 10) - 1;
    const da = parseInt(clean.slice(6, 8), 10);
    const hr = parseInt(clean.slice(8, 10), 10);
    const mn = parseInt(clean.slice(10, 12), 10);
    const sc = parseInt(clean.slice(12, 14), 10);
    if (!isNaN(yr) && !isNaN(mo) && !isNaN(da) && !isNaN(hr) && !isNaN(mn)) {
      return Date.UTC(yr, mo, da, hr, mn, isNaN(sc) ? 0 : sc);
    }
  }
  return 0;
}

export interface RadarFrame {
  time: number; // Unix timestamp in seconds
  path: string; // NOAA MRMS scan identifier
  tileUrl: string;
  formattedTime: string;
}

export interface SatelliteFrame {
  time: number;
  path?: string;
  isoTime: string;
  formattedTime: string;
  tileUrl: string;
  blendUrls?: {
    goes_composite?: string;
    goes_east?: string;
    goes_west?: string;
    himawari?: string;
    meteosat?: string;
  };
}

export class LiveWeatherService {
  private static instance: LiveWeatherService;
  
  private radarFrames: RadarFrame[] = [];
  private currentFrameIndex: number = 0;
  private playbackSpeedMs: number = 100; // default 1x (100ms, 10 fps)

  private satelliteFrames: SatelliteFrame[] = [];
  private currentSatelliteFrameIndex: number = 0;
  private lastSatelliteCacheKey: string = '';

  public static getInstance(): LiveWeatherService {
    if (!LiveWeatherService.instance) {
      LiveWeatherService.instance = new LiveWeatherService();
    }
    return LiveWeatherService.instance;
  }

  public getPlaybackSpeedMs(): number {
    return this.playbackSpeedMs;
  }

  public setPlaybackSpeedMs(speedMs: number): void {
    this.playbackSpeedMs = speedMs;
  }

  /**
   * Fetch NOAA MRMS radar frames from manifest
   */
  public async fetchRadarMetadata(): Promise<RadarFrame[]> {
    const state = store.getState();
    if (state.manifest) {
      this.updateRadarFramesFromManifest(state.manifest);
    }
    return this.radarFrames;
  }

  private lastManifestScansKey: string = '';

  /**
   * Generates Radar frames from NOAA MRMS scans in manifest (supports 6-hour timeline).
   */
  public updateRadarFramesFromManifest(manifest: any): RadarFrame[] {
    const radarScans: string[] = manifest?.live_weather?.mrms?.scans || [];
    const ltScans: string[] = manifest?.live_weather?.mrms_lightning?.scans || [];
    const scans: string[] = radarScans.length > 0 ? radarScans : ltScans;
    if (!scans || scans.length === 0) {
      return this.radarFrames;
    }
    const token = authService.getSessionToken() || '';
    const scansKey = `${manifest?.mode || ''}_${scans.length}_${scans[scans.length - 1]}_${token}`;
    if (this.radarFrames.length > 0 && this.lastManifestScansKey === scansKey) {
      return this.radarFrames;
    }
    this.lastManifestScansKey = scansKey;

    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const apiServer = manifest?.api_server || '';

    const mrmsFrames: RadarFrame[] = scans.map(scan => {
      const ms = parseIsoScanToMs(scan);
      const dateObj = new Date(ms);
      const hours = String(dateObj.getUTCHours()).padStart(2, '0');
      const mins = String(dateObj.getUTCMinutes()).padStart(2, '0');
      // Tiered 2K/4K resolution: desktop screens (>= 1200px or high-DPI) request crisp 4K tier (_4096.webp)
      const isDesktop = typeof window !== 'undefined' && (window.innerWidth >= 1200 || (window.innerWidth >= 992 && (window.devicePixelRatio || 1) >= 1.5));
      const resSuffix = isDesktop ? '_4096' : '';
      let rawPath = cleanUrl(`${apiServer}/${tileRoot}/observed/mrms/mrms_${scan}${resSuffix}.webp`);
      if (!rawPath.startsWith('http://') && !rawPath.startsWith('https://')) {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        let pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
        pathname = pathname.substring(0, pathname.lastIndexOf('/') + 1);
        const cleanPath = rawPath.startsWith('/') ? rawPath.substring(1) : rawPath;
        rawPath = cleanUrl(`${origin}${pathname}${cleanPath}`);
      }
      rawPath = appendAuthToken(rawPath);

      return {
        time: Math.floor(ms / 1000),
        path: scan,
        tileUrl: rawPath,
        formattedTime: `${hours}:${mins} UTC`
      };
    });

    if (mrmsFrames.length > 0) {
      this.radarFrames = mrmsFrames;
      if (this.currentFrameIndex >= this.radarFrames.length) {
        this.currentFrameIndex = Math.max(0, this.radarFrames.length - 1);
      }
    }
    return this.radarFrames;
  }

  public getRadarFrames(loopHours?: number): RadarFrame[] {
    const state = store.getState();
    if (
      (state.manifest?.live_weather?.mrms?.scans && state.manifest.live_weather.mrms.scans.length > 0) ||
      (state.manifest?.live_weather?.mrms_lightning?.scans && state.manifest.live_weather.mrms_lightning.scans.length > 0)
    ) {
      this.updateRadarFramesFromManifest(state.manifest);
    }
    if (this.radarFrames.length === 0) return [];
    if (loopHours === undefined || loopHours === null) {
      return this.radarFrames;
    }
    const maxTimeSec = this.radarFrames[this.radarFrames.length - 1].time;
    const minTimeSec = maxTimeSec - (loopHours * 3600);
    const filtered = this.radarFrames.filter(f => f.time >= minTimeSec);
    const baseFrames = filtered.length > 0 ? filtered : this.radarFrames;

    // Subsample to 4-minute cadence for 8h playback performance (~120 frames instead of ~240)
    if (loopHours >= 8 && baseFrames.length > 120) {
      const subsampled: RadarFrame[] = [];
      for (let i = 0; i < baseFrames.length; i += 2) {
        subsampled.push(baseFrames[i]);
      }
      if (subsampled.length > 0 && subsampled[subsampled.length - 1].time !== baseFrames[baseFrames.length - 1].time) {
        subsampled.push(baseFrames[baseFrames.length - 1]);
      }
      return subsampled;
    }

    return baseFrames;
  }

  public getCurrentFrameIndex(): number {
    return this.currentFrameIndex;
  }

  public getClosestRadarFrameIndex(targetDate: Date, referenceNow?: Date): number {
    if (this.radarFrames.length === 0) return 0;
    const nowDate = referenceNow || ManifestTimelineHelper.getNowDate(store.getState().timelineRange);
    const diffMs = targetDate.getTime() - nowDate.getTime();
    const offsetHours = diffMs / (3600 * 1000);

    // If at or after NOW, return the latest available scan
    if (offsetHours >= -0.01) {
      return this.radarFrames.length - 1;
    }

    const latestScanMs = this.radarFrames[this.radarFrames.length - 1].time * 1000;
    const targetScanMs = latestScanMs + offsetHours * 3600 * 1000;

    let bestIdx = 0;
    let minDiff = Infinity;
    this.radarFrames.forEach((frame, idx) => {
      const frameMs = frame.time > 10000000000 ? frame.time : frame.time * 1000;
      const diff = Math.abs(frameMs - targetScanMs);
      if (diff < minDiff) {
        minDiff = diff;
        bestIdx = idx;
      }
    });
    return bestIdx;
  }

  public setCurrentFrameIndex(index: number): void {
    if (index >= 0 && index < this.radarFrames.length) {
      this.currentFrameIndex = index;
    } else if (this.radarFrames.length > 0) {
      this.currentFrameIndex = Math.max(0, Math.min(index, this.radarFrames.length - 1));
    }
  }

  public getCurrentRadarFrame(_loopHours?: number): RadarFrame | null {
    if (this.radarFrames.length === 0) return null;
    return this.radarFrames[this.currentFrameIndex] || this.radarFrames[this.radarFrames.length - 1];
  }

  public nextFrame(loopHours?: number): RadarFrame | null {
    if (this.radarFrames.length === 0) return null;
    const loopFrames = this.getRadarFrames(loopHours);
    if (loopFrames.length === 0) return null;

    const currentFrame = this.radarFrames[this.currentFrameIndex];
    let currentLoopIdx = -1;
    if (currentFrame) {
      currentLoopIdx = loopFrames.findIndex(f => f.time === currentFrame.time);
    }

    if (currentLoopIdx === -1 || currentLoopIdx >= loopFrames.length - 1) {
      currentLoopIdx = 0; // Wrap around to oldest frame in loop
    } else {
      currentLoopIdx += 1; // Advance forward in time toward newest
    }

    const nextLoopFrame = loopFrames[currentLoopIdx];
    const globalIdx = this.radarFrames.findIndex(f => f.time === nextLoopFrame.time);
    this.currentFrameIndex = globalIdx !== -1 ? globalIdx : 0;
    return nextLoopFrame;
  }

  /**
   * Generates Satellite timeline frames for satellite scans (supports multi-select sources and N-hour filtering).
   */
  public getSatelliteFrames(hoursOrCount?: number, source: string | string[] = ['goes_composite', 'himawari', 'meteosat']): SatelliteFrame[] {
    const state = store.getState();
    const isTransparent = state.liveWeatherSatRenderMode === 'transparent';

    const selectedSources: string[] = Array.isArray(source)
      ? source
      : (source === 'global_blend' ? ['goes_composite', 'himawari', 'meteosat'] : [source]);

    if (selectedSources.length === 0) {
      this.satelliteFrames = [];
      return [];
    }

    const tileRoot = (state.manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const apiServer = state.manifest?.api_server || '';

    const getActivePrefixForSource = (src: string) => {
      const preferred = isTransparent ? `${src}_transparent` : src;
      const fallback = isTransparent ? src : `${src}_transparent`;
      const prefScans = (state.manifest as any)?.live_weather?.satellite?.[preferred]?.scans;
      if (prefScans && prefScans.length > 0) {
        return preferred;
      }
      const fallbackScans = (state.manifest as any)?.live_weather?.satellite?.[fallback]?.scans;
      if (fallbackScans && fallbackScans.length > 0) {
        return fallback;
      }
      return preferred;
    };

    const getUrlForSourceAndScan = (src: string, scan: string) => {
      const activePfx = getActivePrefixForSource(src);
      let rawUrl = cleanUrl(`${apiServer}/${tileRoot}/observed/satellite/${activePfx}_${scan}.webp`);
      if (!apiServer && rawUrl.startsWith('/')) {
        rawUrl = rawUrl.substring(1);
      }
      return appendAuthToken(rawUrl);
    };

    const getScansForSource = (src: string): Array<{ scan: string, time: number }> => {
      const activePfx = getActivePrefixForSource(src);
      const satObj = (state.manifest as any)?.live_weather?.satellite?.[activePfx];
      if (!satObj || !satObj.scans || satObj.scans.length === 0) return [];
      return satObj.scans.map((scan: string) => {
        let timeMs = 0;
        if (scan.length >= 11) {
          const yr = parseInt(scan.slice(0, 4), 10);
          const doy = parseInt(scan.slice(4, 7), 10);
          const hr = parseInt(scan.slice(7, 9), 10);
          const mn = parseInt(scan.slice(9, 11), 10);
          if (!isNaN(yr) && !isNaN(doy) && !isNaN(hr) && !isNaN(mn)) {
            timeMs = Date.UTC(yr, 0, 1) + (doy - 1) * 86400000 + hr * 3600000 + mn * 60000;
          }
        }
        return { scan, time: timeMs };
      });
    };

    const findClosestScan = (scansList: Array<{ scan: string, time: number }>, targetTime: number): string | null => {
      if (scansList.length === 0) return null;
      let closestScan: string | null = null;
      let minDiff = Infinity;
      for (const item of scansList) {
        const diff = Math.abs(item.time - targetTime);
        if (diff < minDiff) {
          minDiff = diff;
          closestScan = item.scan;
        }
      }
      return closestScan;
    };

    let anchorSource = selectedSources.find(src => getScansForSource(src).length > 0) || selectedSources[0];
    const allAnchorScans = getScansForSource(anchorSource);

    if (allAnchorScans.length === 0) {
      this.satelliteFrames = [];
      return [];
    }

    const token = authService.getSessionToken() || '';
    const scansCount = allAnchorScans.length;
    const latestScan = allAnchorScans[allAnchorScans.length - 1]?.scan || '';
    const satCacheKey = `${selectedSources.slice().sort().join(',')}_${isTransparent}_${scansCount}_${latestScan}_${token}`;

    if (this.satelliteFrames.length === 0 || this.lastSatelliteCacheKey !== satCacheKey) {
      this.lastSatelliteCacheKey = satCacheKey;

      // Always populate this.satelliteFrames with the full 8-hour window (or all available scans)
      const maxTimeMs = Math.max(...allAnchorScans.map(s => s.time));
      const fullWindowCutoffMs = maxTimeMs - (8 * 3600 * 1000);
      let anchorScans = allAnchorScans.filter(s => s.time >= fullWindowCutoffMs);
      if (anchorScans.length === 0) {
        anchorScans = allAnchorScans;
      }

      const sourceScansMap: Record<string, Array<{ scan: string, time: number }>> = {};
      for (const src of selectedSources) {
        sourceScansMap[src] = getScansForSource(src);
      }

      this.satelliteFrames = anchorScans.map(anchor => {
        const targetTime = anchor.time;
        const blendUrls: Record<string, string> = {};

        for (const src of selectedSources) {
          const resolvedScan = src === anchorSource ? anchor.scan : findClosestScan(sourceScansMap[src] || [], targetTime);
          if (resolvedScan) {
            blendUrls[src] = getUrlForSourceAndScan(src, resolvedScan);
          }
        }

        const dt = new Date(targetTime);
        const mStr = String(dt.getUTCMonth() + 1).padStart(2, '0');
        const dStr = String(dt.getUTCDate()).padStart(2, '0');
        const hStr = String(dt.getUTCHours()).padStart(2, '0');
        const minStr = String(dt.getUTCMinutes()).padStart(2, '0');
        const formattedTime = `${dt.getUTCFullYear()}-${mStr}-${dStr} ${hStr}:${minStr} UTC`;

        return {
          time: targetTime,
          path: anchor.scan,
          isoTime: anchor.scan,
          formattedTime,
          tileUrl: blendUrls[selectedSources[0]] || Object.values(blendUrls)[0] || '',
          blendUrls
        };
      });

      if (this.currentSatelliteFrameIndex >= this.satelliteFrames.length) {
        this.currentSatelliteFrameIndex = Math.max(0, this.satelliteFrames.length - 1);
      }
    }

    // If loopHours is specified and less than 8, return filtered subset for playback loop
    if (typeof hoursOrCount === 'number' && hoursOrCount > 0 && hoursOrCount < 8) {
      const maxTimeMs = Math.max(...this.satelliteFrames.map(s => s.time > 10000000000 ? s.time : s.time * 1000));
      const loopCutoffMs = maxTimeMs - (hoursOrCount * 3600 * 1000);
      const filtered = this.satelliteFrames.filter(s => {
        const t = s.time > 10000000000 ? s.time : s.time * 1000;
        return t >= loopCutoffMs;
      });
      return filtered.length > 0 ? filtered : this.satelliteFrames;
    }

    return this.satelliteFrames;
  }

  public getCurrentSatelliteFrameIndex(): number {
    return this.currentSatelliteFrameIndex;
  }

  public getClosestSatelliteFrameIndex(targetDate: Date, referenceNow?: Date): number {
    if (this.satelliteFrames.length === 0) return 0;
    const nowDate = referenceNow || ManifestTimelineHelper.getNowDate(store.getState().timelineRange);
    const diffMs = targetDate.getTime() - nowDate.getTime();
    const offsetHours = diffMs / (3600 * 1000);

    // If at or after NOW, return latest available scan
    if (offsetHours >= -0.01) {
      return this.satelliteFrames.length - 1;
    }

    const lastFrame = this.satelliteFrames[this.satelliteFrames.length - 1];
    const latestScanMs = lastFrame.time > 10000000000 ? lastFrame.time : lastFrame.time * 1000;
    const targetScanMs = latestScanMs + offsetHours * 3600 * 1000;

    let bestIdx = 0;
    let minDiff = Infinity;
    this.satelliteFrames.forEach((frame, idx) => {
      const frameMs = frame.time > 10000000000 ? frame.time : frame.time * 1000;
      const diff = Math.abs(frameMs - targetScanMs);
      if (diff < minDiff) {
        minDiff = diff;
        bestIdx = idx;
      }
    });
    return bestIdx;
  }

  public setSatelliteFrameIndex(index: number): void {
    if (index >= 0 && index < this.satelliteFrames.length) {
      this.currentSatelliteFrameIndex = index;
    } else if (this.satelliteFrames.length > 0) {
      this.currentSatelliteFrameIndex = Math.max(0, Math.min(index, this.satelliteFrames.length - 1));
    }
  }

  public getCurrentSatelliteFrame(): SatelliteFrame | null {
    if (this.satelliteFrames.length === 0) return null;
    return this.satelliteFrames[this.currentSatelliteFrameIndex] || this.satelliteFrames[this.satelliteFrames.length - 1];
  }

  public nextSatelliteFrame(loopHours?: number): SatelliteFrame | null {
    if (this.satelliteFrames.length === 0) return null;
    const effectiveLoopHours = loopHours || store.getState().liveWeatherSatelliteLoopHours || 8;
    const loopFrames = this.getSatelliteFrames(effectiveLoopHours, store.getState().liveWeatherSatelliteSources);
    if (loopFrames.length === 0) return null;

    const currentFrame = this.satelliteFrames[this.currentSatelliteFrameIndex];
    let currentLoopIdx = -1;
    if (currentFrame) {
      currentLoopIdx = loopFrames.findIndex(f => f.time === currentFrame.time);
    }

    if (currentLoopIdx === -1 || currentLoopIdx >= loopFrames.length - 1) {
      currentLoopIdx = 0; // Wrap around to oldest frame in loop
    } else {
      currentLoopIdx += 1; // Advance forward in time toward newest
    }

    const nextLoopFrame = loopFrames[currentLoopIdx];
    const globalIdx = this.satelliteFrames.findIndex(f => f.time === nextLoopFrame.time);
    this.currentSatelliteFrameIndex = globalIdx !== -1 ? globalIdx : 0;
    return nextLoopFrame;
  }


  /**
   * Returns NASA GIBS Satellite tile URL for GOES-West or GOES-East
   */
  public getSatelliteTileUrl(_source: 'goes_composite' | 'goes_east' | 'goes_west' | 'global_ir' | 'himawari' | 'meteosat' = 'goes_composite'): string {
    const frame = this.getCurrentSatelliteFrame();
    if (frame) return frame.tileUrl;
    const layerName = 'GOES-East_ABI_GeoColor';
    return `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/${layerName}/default/default/GoogleMapsCompatible_Level7/{z}/{y}/{x}.jpg`;
  }

  private cachedAqiStationsGeoJson: any = null;
  private lastAqiFetchTime: number = 0;
  private lastAqiMtimeEpoch: number = 0;
  private aqiFetchInFlight: Promise<any | null> | null = null;

  public getCachedAqiStationsGeoJson(): any | null {
    return this.cachedAqiStationsGeoJson;
  }

  /**
   * Shared, authenticated, in-flight deduplicated fetcher for observed/aqi_stations/aqi_stations.json.
   * Shared by both LiveWeatherLayer (map rendering) and Glance2x2Widget (POI nearest station card).
   */
  public async fetchAqiStationsGeoJson(forceRefresh: boolean = false): Promise<any | null> {
    const state = store.getState();
    const manifest = state.manifest as any;
    const mtime = Number(manifest?.aqi_stations?.mtime_epoch) || 0;
    const now = Date.now();

    if (
      !forceRefresh &&
      this.cachedAqiStationsGeoJson &&
      now - this.lastAqiFetchTime < 15 * 60 * 1000 &&
      (mtime === 0 || mtime === this.lastAqiMtimeEpoch)
    ) {
      return this.cachedAqiStationsGeoJson;
    }

    if (this.aqiFetchInFlight) {
      return this.aqiFetchInFlight;
    }

    let apiServer = manifest?.api_server;
    if (apiServer === undefined || apiServer === null) {
      apiServer = 'https://auth.skyskout.com/';
    } else if (apiServer === '' && typeof window !== 'undefined') {
      const path = window.location.pathname;
      const dir = path.substring(0, path.lastIndexOf('/') + 1);
      apiServer = window.location.origin + dir;
    }
    if (apiServer && !apiServer.endsWith('/')) {
      apiServer += '/';
    }

    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const cacheBust = mtime || Math.floor(now / 1800000);
    const rawUrl = `${apiServer || ''}${tileRoot}/observed/aqi_stations/aqi_stations.json?_t=${cacheBust}`;
    const fetchUrl = appendAuthToken(rawUrl);

    const token = authService.getSessionToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    this.aqiFetchInFlight = (async () => {
      try {
        const res = await fetch(fetchUrl, {
          headers,
          credentials: fetchUrl.includes('auth.skyskout.com') ? 'include' : 'same-origin'
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const geoJson = await res.json();
        if (geoJson && geoJson.type === 'FeatureCollection' && Array.isArray(geoJson.features)) {
          this.cachedAqiStationsGeoJson = geoJson;
          this.lastAqiFetchTime = Date.now();
          this.lastAqiMtimeEpoch = mtime;
          return geoJson;
        }
        return this.cachedAqiStationsGeoJson;
      } catch (err) {
        console.warn('[LiveWeatherService] Failed to load AQI stations:', err);
        return this.cachedAqiStationsGeoJson;
      } finally {
        this.aqiFetchInFlight = null;
      }
    })();

    return this.aqiFetchInFlight;
  }
}

export default LiveWeatherService;
